import { useMemo } from 'react';

import { FocusableItem, ListReservedValues, ValueListItem } from '../stateful-list.types';

export type UseFocusableItemsProps = {
  /**
   * We may have multiple elements on the DOM
   * that uses the list, so we need a unique identifier
   */
  id: string;

  /** A filtered list that contains only the value list item */
  items: ValueListItem[];

  /** Whether the list uses search field */
  useSearch: boolean;

  /** Whether the list uses select all  */
  useSelectAll: boolean;

  /** Callback for select all action */
  selectAllCallback?: () => void;
};

export function useFocusableItems({
  id,
  items,
  useSearch,
  useSelectAll,
  selectAllCallback,
}: UseFocusableItemsProps) {
  return useMemo(() => {
    const normalizedId = id.replace(/[^a-zA-Z0-9]/g, '-');
    const focusableItems: FocusableItem[] = items.map((item) => ({
      id: item.focusId,
      value: item.value,
      onItemSelect: item.itemCallback,
    }));

    if (useSelectAll) {
      focusableItems.unshift({
        id: `focusable-item-select-all-${normalizedId}`,
        value: ListReservedValues.selectAll,
        onItemSelect: selectAllCallback,
      });
    }

    if (useSearch) {
      focusableItems.unshift({
        id: `focusable-item-search-input-${normalizedId}`,
        value: ListReservedValues.search,
        onItemSelect: undefined, // Search input does not have a callback
      });
    }

    return focusableItems;
  }, [id, items, selectAllCallback, useSearch, useSelectAll]);
}
